import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ChevronRight, Factory, Cog, Droplets, Wind, Train, Building2, Layers, Sparkles, Fan, Settings, CircleDot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, services, clients, collaborations, statistics, industriesServed, capabilities } from '../data/mock';

const serviceIcons = {
  train: Train,
  crane: Building2,
  conveyor: Cog,
  building: Building2,
  factory: Factory,
  droplets: Droplets,
  wind: Wind,
  fan: Fan,
  layers: Layers,
  sparkles: Sparkles,
  beam: Settings,
  wheel: CircleDot,
  cog: Cog,
  rail: Cog
};

const Home = () => {
  const featuredServices = services.slice(0, 6);
  const displayStats = [
    { value: "SINCE 1988", label: "Year Established" },
    { value: "35+", label: "Years of Excellence" },
    { value: "500+", label: "Total Workforce" },
    { value: "20+", label: "Years in Stacker Reclaimer" }
  ];

  return (
    <div className="bg-[#0b0b0b]">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black pt-16 sm:pt-0">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px),
                             repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px)`
          }} />
        </div>
        
        {/* Yellow Accent Line */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#b00000] via-red-600 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#b00000]/10 border border-[#b00000]/30 px-3 sm:px-4 py-2 mb-4 sm:mb-6">
                <span className="w-2 h-2 bg-[#b00000] rounded-none full animate-pulse" />
                <span className="text-gray-300 text-xs sm:text-sm tracking-wide font-medium">SINCE 1988 • Excellence in Engineering</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6">
                {companyInfo.tagline}
              </h1>
              
              <p className="text-base sm:text-lg text-[#c0c0c0] leading-relaxed mb-6 sm:mb-8 max-w-xl">
                {companyInfo.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/services" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-6 sm:px-8 h-12 sm:h-14 text-sm sm:text-base transition-all duration-300 group ">
                    Explore Services
                    <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto bg-transparent border-white/20 text-white hover:bg-[#0b0b0b] rounded-none none px-6 sm:px-8 h-12 sm:h-14 text-sm sm:text-base transition-all duration-300">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6 lg:mt-0">
              {displayStats.map((stat, index) => (
                <div 
                  key={index}
                  className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-6 hover:border-[#b00000]/50 hover: hover:shadow-red-600/10 transition-all duration-300"
                >
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#b00000] mb-1 sm:mb-2">{stat.value}</div>
                  <div className="text-[#c0c0c0] text-xs sm:text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="py-12 sm:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">About the Company</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
                Engineering Excellence SINCE 1988
              </h2>
              <p className="text-sm sm:text-base text-[#c0c0c0] leading-relaxed mb-4 sm:mb-6">
                Our story began in 1988 with the goal of enhancing Coal Handling Plant (CHP) operations 
                in Thermal Power Stations. Starting with Wagon Tippler maintenance, we've 
                evolved into a prominent name, offering complete CHP services across the country.
              </p>
              <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {['Complete CHP Solutions', 'Thermal Power Plant Expertise', 'NTPC & NSPCL Enlisted', 'Nationwide Service Network', '300+ Skilled Workforce'].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 sm:gap-3">
                    <CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5 text-[#b00000] flex-shrink-0" />
                    <span className="text-gray-300 text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/about">
                <Button variant="outline" className="w-full sm:w-auto bg-transparent border-white/20 text-white hover:bg-[#0b0b0b] rounded-none none px-6 h-11 sm:h-12 transition-all duration-300 group">
                  Learn More About Us
                  <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="relative mt-6 lg:mt-0">
              <div className="aspect-square overflow-hidden border border-white/10 ">
                <img 
                  src="/images/company-operations.png" 
                  alt="Elemech Engineering Operations - Coal Handling Plants and Industrial Equipment"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 bg-[#b00000] p-4 sm:p-6 ">
                <div className="text-white text-2xl sm:text-3xl font-bold">35+</div>
                <div className="text-white/90 text-xs sm:text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Services Overview */}
      <section className="py-12 sm:py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Our Services</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              Comprehensive CHP Solutions
            </h2>
            <p className="text-sm sm:text-base text-[#c0c0c0] max-w-2xl mx-auto px-4">
              From Wagon Tipplers to FGD systems, we provide complete operation, maintenance, 
              and structural restoration services for thermal power plants.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {featuredServices.map((service) => {
              const IconComponent = serviceIcons[service.icon] || Cog;
              return (
                <Card key={service.id} className="bg-[#0b0b0b] border-white/10 hover:border-[#b00000]/50 hover: hover:shadow-red-600/10 transition-all duration-300 group rounded-none none">
                  <CardContent className="p-4 sm:p-6">
                    <div className="w-10 sm:w-12 h-10 sm:h-12 bg-[#b00000]/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#b00000] transition-colors">
                      <IconComponent className="w-5 sm:w-6 h-5 sm:h-6 text-[#b00000] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-[#c0c0c0] text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">{service.shortDesc}</p>
                    <Link to="/services" className="text-[#b00000] text-xs sm:text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">
                      Learn More <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-8 sm:mt-10">
            <Link to="/services">
              <Button className="w-full sm:w-auto bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-6 sm:px-8 h-11 sm:h-12 transition-all duration-300 ">
                View All 14 Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-12 sm:py-16 border-t border-white/10 bg-[#0b0b0b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-10">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Industries We Serve</span>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
              Powering India's Energy Sector
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {industriesServed.map((industry, index) => (
              <div key={index} className="bg-[#0b0b0b] border border-white/10 px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-base text-gray-300 hover:border-[#b00000]/50 hover: hover:shadow-red-600/10 hover:text-white transition-all">
                {industry}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organizations We Serve */}
      <section className="py-12 sm:py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Organizations We Serve</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              Trusted by Industry Leaders
            </h2>
            <p className="text-sm sm:text-base text-[#c0c0c0] max-w-2xl mx-auto px-4">
              We are proudly enlisted with major National Power Corporation units across India.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
            {clients.map((client, index) => (
              <div 
                key={index}
                className="bg-[#0b0b0b] border border-white/10 p-3 sm:p-6 flex items-center justify-center hover:border-[#b00000]/50 hover: hover:shadow-red-600/10 transition-all group h-28 sm:h-44"
              >
                <div className="bg-white p-2 sm:p-4 w-full h-full flex items-center justify-center">
                  <img 
                    src={client.logo} 
                    alt={client.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Our Collaborations */}
          <div className="text-center mb-6 sm:mb-8">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Our Collaborations</span>
            <p className="text-sm sm:text-base text-[#c0c0c0]">Authorized dealer & partner of global brands</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {collaborations.map((partner, index) => (
              <div 
                key={index}
                className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-6 hover:border-[#b00000]/50 hover: hover:shadow-red-600/10 transition-all group text-center"
              >
                <div className="bg-white p-3 sm:p-4 mb-3 sm:mb-4 h-16 sm:h-24 flex items-center justify-center">
                  <img 
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <span className="text-white font-semibold text-base sm:text-lg block mb-1">{partner.name}</span>
                <span className="text-[#b00000] text-xs sm:text-sm">{partner.country}</span>
                <p className="text-[#c0c0c0] text-xs mt-2 hidden sm:block">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 border-t border-white/10 bg-gradient-to-r from-[#b00000] to-[#b00000]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8 text-center lg:text-left">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                Ready to Partner with Us?
              </h2>
              <p className="text-sm sm:text-base text-white/90 max-w-xl">
                Get in touch to discuss your Coal Handling Plant requirements and discover how our 
                35+ years of experience can optimize your operations.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-6 sm:px-8 h-12 sm:h-14 text-sm sm:text-base transition-all duration-300 group ">
                  Get a Quote
                  <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/projects" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto bg-white border-white text-black hover:bg-white hover:text-black active:bg-white active:text-black focus:bg-white focus:text-black rounded-none none px-6 sm:px-8 h-12 sm:h-14 text-sm sm:text-base transition-all duration-300 font-semibold">
                  View Our Projects
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
